import { FormattedMessage } from "react-intl";
import type { ReportSectionMeta } from "~/api/model";
import { Progress } from "~/components/ui/progress";
import { cn } from "~/lib/utils";
import { getReportProgress } from "./reportGenerationProgress";
import { useReportGenerationEta } from "./useReportGenerationEta";

type ReportGenerationProgressProps = {
  reportId: string;
  sections: ReadonlyArray<ReportSectionMeta>;
  isGenerating: boolean;
};

export const ReportGenerationProgress = ({
  reportId,
  sections,
  isGenerating,
}: ReportGenerationProgressProps) => {
  const progress = getReportProgress(sections);
  const estimatedRemainingMinutes = useReportGenerationEta(reportId, sections);

  if (!isGenerating || progress.isComplete) {
    return null;
  }

  const isUnknownTotal = sections.length === 0;
  const remainingSections =
    progress.totalCount - progress.completedCount - progress.failedCount;

  return (
    <div className="mb-4 space-y-1.5" aria-live="polite">
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="font-medium text-foreground">
          {isUnknownTotal ? (
            <FormattedMessage
              id="reportArea.generatingProgressUnknown"
              defaultMessage="Generating report"
            />
          ) : (
            <FormattedMessage
              id="reportArea.generatingProgress"
              defaultMessage="Generating report — {completed} of {total} sections"
              values={{
                completed: progress.completedCount + progress.failedCount,
                total: progress.totalCount,
              }}
            />
          )}
        </span>
        {!isUnknownTotal && <span>{progress.percentage}%</span>}
      </div>
      <Progress
        value={isUnknownTotal ? 0 : progress.percentage}
        animated={isGenerating}
        className={cn(
          "h-1.5",
          progress.hasFailed
            ? "bg-red-100 [&>div]:bg-red-500"
            : "bg-blue-100 [&>div]:bg-blue-500"
        )}
      />
      {!isUnknownTotal &&
        estimatedRemainingMinutes !== null &&
        remainingSections > 0 && (
          <p className="text-xs text-muted-foreground">
            {estimatedRemainingMinutes < 1 ? (
              <FormattedMessage
                id="reportArea.generatingProgress.lessThanMinute"
                defaultMessage="Less than 1 min remaining"
              />
            ) : (
              <FormattedMessage
                id="reportArea.generatingProgress.eta"
                defaultMessage="About {minutes} min remaining"
                values={{ minutes: estimatedRemainingMinutes }}
              />
            )}
          </p>
        )}
    </div>
  );
};
